import ScChip from '@garmin/support-components/src/components/ScChip/index.vue';
import ScAutocomplete from '@garmin/support-components/src/components/ScAutocomplete/index.vue';
import { FuzzySearch } from '@garmin/support-fuzzy-search';
import { fireUtagEvent } from '@garmin/support-analytics';
import { mapActions, mapState } from 'pinia';
import { useContentStore } from '../../stores/content/index.js';
import { useGlobalStore } from '../../stores/global/index.js';
import { useProductStore } from '../../stores/product/index.js';
import TranslationsMixin from '../../mixins/translations';
import QueryToNameConverterMixin from '../../mixins/queryToNameConverter';
import '@garmin/regalia/icons/close/icon';

export default {
  name: 'SearchFilters',
  mixins: [TranslationsMixin, QueryToNameConverterMixin],
  components: {
    ScChip,
    ScAutocomplete,
  },
  props: {
    selectedKeyword: {
      type: String,
      default: '',
    },
    selectedProductFamily: {
      type: String,
      default: '',
    },
    selectedSubtopic: {
      type: String,
      default: '',
    },
    selectedProduct: {
      type: [String, Object],
      default: '',
    },
    hasProductSearchError: {
      type: Boolean,
      default: false,
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalProductSearch: '',
      autocompleteFilterFunc: FuzzySearch.isFuzzyMatch,
      internalKeyword: this.selectedKeyword,
      internalSelectedProductFamily: this.selectedProductFamily,
      internalSubtopic: this.selectedSubtopic,
      internalSelectedProduct: this.selectedProduct,
      isActive: this.expanded,
    };
  },
  mounted() {
    this.isActive = this.isMobile && this.getInitialKeywordSearch ? true : !this.isMobile;
  },
  watch: {
    selectedProduct() {
      this.internalSelectedProduct = this.selectedProduct;
    },
    selectedKeyword() {
      this.internalKeyword = this.selectedKeyword;
    },
    selectedProductFamily() {
      this.internalSelectedProductFamily = this.selectedProductFamily;
    },
    selectedSubtopic() {
      this.internalSubtopic = this.selectedSubtopic;
    },
    expanded() {
      this.isActive = this.expanded;
    },
  },
  computed: {
    ...mapState(useGlobalStore, ['systemConfig', 'getTagListTransformed']),

    ...mapState(useProductStore, [
      'productList',
      'productDetails',
      'getProductSelectedIdentifier',
      'findSelectedProductInListByIdentifier',
      'productFamilyList',
    ]),

    ...mapState(useContentStore, ['subTopics']),
    isMobile() {
      return (/iphone|ipod|android|ie|blackberry|fennec/).test(navigator.userAgent.toLowerCase());
    },
    hasResults() {
      return (results) => (
        results
        && Array.isArray(results)
        && results.length > 0
      );
    },

    hasMadeAnySelections() {
      return (
        this.getInitialKeywordSearch
        || this.getInitialSubTopicName
        || this.getInitialSelectedProductFamilyName
        || this.getInitialSelectedProductName
      );
    },

    hasAttemptedSearch() {
      return (
        this.internalKeyword
        || this.internalSelectedProduct
        || this.internalSelectedProductFamily
        || this.internalSubtopic
        || this.internalProductSearch
      );
    },

    canFilterChange() {
      return this.hasMadeAnySelections || this.hasAttemptedSearch;
    },
  },
  methods: {
    ...mapActions(useProductStore, ['resetProductDetails', 'toggleProductSearchError']),
    onHeadingCtaClick() {
      this.isActive = !this.isActive;
      this.$emit('onHeadingCta', this.isActive);
    },
    removeKeywordSelection() {
      this.internalKeyword = '';
      this.$emit('onSearchFilterKeywordChange', this.internalKeyword);
    },
    removeProductFamilySelection() {
      this.internalSelectedProductFamily = '';
      this.$emit('onSearchFilterProductFamilyChange', this.internalSelectedProductFamily);
    },
    removeSubtopicSelection() {
      this.internalSubtopic = '';
      this.$emit('onSearchFilterSubtopicChange', this.internalSubtopic);
    },
    removeProductSelection() {
      this.internalSelectedProduct = '';
      this.internalProductSearch = '';
      this.resetProductDetails();
      this.$emit('onSearchFilterProductCleared', this.internalSelectedProduct);
    },
    onSearchFilterProductSelected(termOrItem) {
      const identifier = this.getProductSelectedIdentifier(termOrItem);

      if (identifier) {
        this.$emit('onSearchFilterProductSelected', { identifier, termOrItem });
        this.internalSelectedProduct = termOrItem;
      } else this.removeProductSelection();
    },

    onSearchFilterProductInput(e) {
      this.toggleProductSearchError(false);
      this.internalProductSearch = e.search;
      this.$emit('onSearchFilterProductInput', e);
    },
    sendFiltersGA() {
      const query = JSON.parse(JSON.stringify(this.$route.query));
      if (this.internalSubtopic && query.issueSubTagName !== this.internalSubtopic) {
        const subTopicDisplayName = this.getFilterDisplayName(this.subTopics, this.internalSubtopic);
        fireUtagEvent({
          event_action: 'Filter',
          event_category: `${this.systemConfig.mode} - Subtopic`,
          event_label: subTopicDisplayName,
        });
      }
      if (this.internalSelectedProductFamily
          && query.productFamily !== this.internalSelectedProductFamily) {
        const productFamilyDisplayName = this.getFilterDisplayName(this.productFamilyList, this.internalSelectedProductFamily);
        fireUtagEvent({
          event_action: 'Filter',
          event_category: `${this.systemConfig.mode} - Topic`,
          event_label: productFamilyDisplayName,
        });
      }
      if (this.internalKeyword && query.keywordSearch !== this.getInitialKeywordSearch) {
        fireUtagEvent({
          event_action: 'Filter',
          event_category: `${this.systemConfig.mode} - Keyword`,
          event_label: this.internalKeyword,
        });
      }
      if (this.internalProductSearch
          && query.identifier !== this.getProductSelectedIdentifier(this.internalSelectedProduct)) {
        const manualAnalyticsEvent = {
          event_action: 'Filter',
          event_category: `${this.systemConfig.mode} - Search`,
          event_label: this.internalProductSearch,
        };

        if (!this.getProductSelectedIdentifier(this.internalSelectedProduct)) {
          manualAnalyticsEvent.event_label = 'Serial Number';
          manualAnalyticsEvent.event_category = `${this.systemConfig.mode} - Serial Number`;
        }
        fireUtagEvent(manualAnalyticsEvent);
      }
    },

    onSearchFilterSubmit() {
      this.sendFiltersGA();
      const search = (this.internalProductSearch || '').toLowerCase().trim();
      const productName = (this.getProductNameFromTermOrItem(this.internalSelectedProduct) || '').toLowerCase().trim();

      if (search && search !== productName) this.onSearchFilterProductSelected(this.internalProductSearch);

      const productSearchIdentifier = this.getProductSelectedIdentifier(
        this.internalSelectedProduct,
      );
      this.isActive = !this.isMobile;
      this.$emit('onSearchFilterSubmit', {
        ...this.internalSubtopic && { issueSubTagName: this.internalSubtopic },
        ...(
          this.internalSelectedProductFamily
          && { productFamily: this.internalSelectedProductFamily }
        ),
        ...this.internalKeyword && { keywordSearch: this.internalKeyword },
        ...productSearchIdentifier && { identifier: productSearchIdentifier },
      });
    },

    onSearchFilterClear() {
      fireUtagEvent({
        event_action: 'Link',
        event_category: `${this.systemConfig.mode} - Search`,
        event_label: 'Clear',
      });

      this.$router.push({ name: 'landing' });
    },
  },
};
